/**
 * The newest release this DEVICE has shown in "Novidades" (`D263`), behind
 * the unseen dot on the Perfil tab and the hub's row.
 *
 * ── ON THE DEVICE, NOT THE SERVER ───────────────────────────────────────────
 * Whether somebody has read a release note is worth one dot, not a table and
 * a route: a second device shows the dot once more, and a reinstall shows
 * only what is still fresh (`unseenNotes`). `D251` keeps its "seen and left"
 * memory on the device for the same reason.
 *
 * One string under {@link NEWS_SEEN_KEY} in Preferences — the version, not a
 * date, so a release note's date can be corrected without re-showing it. The
 * store reads it once, on the first subscriber, and answers `loading` until
 * then: no dot is drawn on a guess.
 */
import { RELEASE_NOTES, type ReleaseNote } from "@repo/contract/community";
import { useMemo, useState, useSyncExternalStore } from "react";
import { preferences } from "../../native";
import { compareVersions, unseenNotes } from "./model";

export const NEWS_SEEN_KEY = "mypocketgym.news.seen";

export type NewsSeenState =
  | { readonly status: "loading" }
  | { readonly seen: string | null; readonly status: "ready" };

export interface NewsSeenStorage {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
}

export interface NewsSeenStore {
  getSnapshot(): NewsSeenState;
  /** Records `version` as seen, unless a newer one already is. */
  markSeen(version: string): void;
  subscribe(listener: () => void): () => void;
}

const LOADING: NewsSeenState = { status: "loading" };

export function createNewsSeenStore(storage: NewsSeenStorage): NewsSeenStore {
  let state: NewsSeenState = LOADING;
  let started = false;
  const listeners = new Set<() => void>();
  const set = (next: NewsSeenState) => {
    state = next;
    for (const listener of listeners) {
      listener();
    }
  };
  const load = () => {
    if (started) {
      return;
    }
    started = true;
    storage
      .get(NEWS_SEEN_KEY)
      .then((seen) => {
        // A mark made while the read was in flight wins.
        if (state.status === "loading") {
          set({ seen, status: "ready" });
        }
      })
      .catch(() => {
        if (state.status === "loading") {
          set({ seen: null, status: "ready" });
        }
      });
  };
  return {
    getSnapshot: () => state,
    markSeen(version) {
      started = true;
      const current = state.status === "ready" ? state.seen : null;
      if (current !== null && compareVersions(current, version) >= 0) {
        return;
      }
      set({ seen: version, status: "ready" });
      storage.set(NEWS_SEEN_KEY, version).catch(() => undefined);
    },
    subscribe(listener) {
      listeners.add(listener);
      load();
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** The app's one store, over Capacitor Preferences. */
export const newsSeenStore = createNewsSeenStore(preferences);

export function useNewsSeen(store: NewsSeenStore = newsSeenStore) {
  return useSyncExternalStore(store.subscribe, store.getSnapshot);
}

/**
 * The release notes still new to this person, empty while the device has not
 * answered. `now` is taken once, when the caller mounts.
 */
export function useUnseenNews(
  store: NewsSeenStore = newsSeenStore,
  notes: readonly ReleaseNote[] = RELEASE_NOTES
): readonly ReleaseNote[] {
  const state = useNewsSeen(store);
  const [now] = useState(() => new Date());
  return useMemo(
    () => (state.status === "ready" ? unseenNotes(notes, state.seen, now) : []),
    [state, notes, now]
  );
}
